import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import CelebrationRounded from '@mui/icons-material/CelebrationRounded';
import EditCalendarRounded from '@mui/icons-material/EditCalendarRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import LockClockRounded from '@mui/icons-material/LockClockRounded';
import PauseCircleRounded from '@mui/icons-material/PauseCircleRounded';
import PlayCircleRounded from '@mui/icons-material/PlayCircleRounded';
import RedeemRounded from '@mui/icons-material/RedeemRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import WorkspacePremiumRounded from '@mui/icons-material/WorkspacePremiumRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, ApiError, unwrap, type Schemas } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { PageHeader } from '../../../components/states';
import { businessDate, dateTime } from '../../../lib/format';
import { errorMessage } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { idempotency, keys, useSubscription } from '../api';
import {
  formatActivationCode,
  isCompleteActivationCode,
  plural,
  redeemErrorText,
  SUBSCRIPTION_EVENT_LABELS,
  subscriptionView,
  type SubscriptionEventKind,
  type SubscriptionStatus,
  type SubscriptionView,
} from '../lib';
import { IconTile, NoAccess } from '../ui';
import { PoweredBy } from '../../../components/PoweredBy';
import { ContactCard } from './ContactCard';
import { FoodpandaCard } from '../../foodpanda/FoodpandaCard';
import { EmailCard } from '../../email/EmailCard';
import { WhatsappCard } from '../../whatsapp/WhatsappCard';

type RedeemResult = Schemas['RedeemActivationCodeResult'];

export function SubscriptionPage() {
  const { can } = useAuth();
  const { data, isLoading, error, refetch } = useSubscription(can('subscription.view'));
  const [redeemed, setRedeemed] = useState<RedeemResult | null>(null);

  if (!can('subscription.view'))
    return (
      <>
        <PageHeader title="Subscription" />
        <NoAccess what="the subscription" />
      </>
    );

  return (
    <>
      <PageHeader
        title="Subscription"
        subtitle="Your plan, how long it runs, and activation codes to extend it."
      />
      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          {errorMessage(error)}
        </Alert>
      ) : isLoading ? (
        <SubscriptionSkeleton />
      ) : !data ? (
        <Alert severity="info">
          This shop has no subscription yet. Redeem an activation code to start one.
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1.5fr) minmax(0,1fr)' },
            alignItems: 'start',
          }}
        >
          <Box sx={{ minWidth: 0, gridColumn: { lg: 1 }, gridRow: { lg: 1 } }}>
            <StatusHero status={data} />
          </Box>
          <Box
            sx={{
              gridColumn: { lg: 2 },
              gridRow: { lg: '1 / span 2' },
              position: { lg: 'sticky' },
              top: { lg: 92 },
            }}
          >
            <Stack spacing={2.5}>
              <RedeemCard onRedeemed={setRedeemed} locked={data.locked} />
              <ContactCard />
            </Stack>
          </Box>
          <Box sx={{ minWidth: 0, gridColumn: { lg: 1 }, gridRow: { lg: 2 } }}>
            <Stack spacing={2.5}>
              <WhatsappCard />
              <EmailCard />
              <FoodpandaCard />
              <History events={data.history} />
            </Stack>
          </Box>
        </Box>
      )}
      {!isLoading && !error && !data && (
        <Box sx={{ mt: 2.5, maxWidth: 520 }}>
          <RedeemCard onRedeemed={setRedeemed} locked />
        </Box>
      )}
      {redeemed && <RedeemedDialog result={redeemed} onClose={() => setRedeemed(null)} />}
      <PoweredBy sx={{ mt: 4 }} />
    </>
  );
}

/** Status card: big remaining-days ring, plan, and the key dates. */
export function StatusHero({ status }: { status: SubscriptionStatus }) {
  const { format } = useShop();
  const view = subscriptionView(status);

  return (
    <Card sx={{ position: 'relative', overflow: 'hidden' }}>
      <Box
        aria-hidden
        sx={(t) => {
          const c = (t.vars ?? t).palette[view.tone].main;
          return {
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(120% 90% at 100% 0%, color-mix(in srgb, ${c} 14%, transparent), transparent 60%)`,
            pointerEvents: 'none',
          };
        }}
      />
      <CardContent sx={{ position: 'relative', p: { xs: 2.5, sm: 3.5 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2.5, sm: 3.5 }}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Ring value={view.remaining} tone={view.tone}>
            <Typography variant="h3" className="tabular" sx={{ lineHeight: 1 }}>
              {status.state === 'GRACE'
                ? Math.max(0, status.daysUntilLock)
                : Math.max(0, status.daysRemaining)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {status.state === 'GRACE' ? 'days to lock' : 'days left'}
            </Typography>
          </Ring>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" color={view.tone} label={view.label} sx={{ fontWeight: 700 }} />
              {status.plan && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<WorkspacePremiumRounded />}
                  label={status.plan.name}
                />
              )}
            </Stack>
            <Typography variant="h4" sx={{ mt: 1.25 }}>
              {view.headline}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {view.detail}
            </Typography>
          </Box>
        </Stack>

        {(status.locked || status.suspended) && (
          <Alert severity="error" icon={<LockClockRounded />} sx={{ mt: 2.5 }}>
            The shop is locked until it is renewed. Your data is safe: redeem a code or contact
            PackBytes.
          </Alert>
        )}

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(3, minmax(0,1fr))' },
          }}
        >
          <DateTile
            icon={<EventAvailableRounded />}
            label="Started"
            value={businessDate(status.startsAt.slice(0, 10), format)}
          />
          <DateTile
            icon={<HourglassBottomRounded />}
            label="Paid through"
            value={businessDate(status.paidThroughDate, format)}
            strong
          />
          <DateTile
            icon={<LockClockRounded />}
            label="Grace period ends"
            hint={`${plural(status.graceDays, 'day')} after paid-through`}
            value={businessDate(status.graceEndsDate, format)}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

function Ring({
  value,
  tone,
  children,
}: {
  value: number;
  tone: SubscriptionView['tone'];
  children: ReactNode;
}) {
  const size = 128;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <Box
      role="img"
      aria-label={`${String(Math.round(value * 100))}% of the period left`}
      sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      <Box component="svg" viewBox={`0 0 ${String(size)} ${String(size)}`} sx={{ width: size }}>
        <Box
          component="circle"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          sx={{ stroke: 'var(--mui-palette-action-hover)' }}
        />
        <Box
          component="circle"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${String(c * value)} ${String(c)}`}
          transform={`rotate(-90 ${String(size / 2)} ${String(size / 2)})`}
          sx={{
            stroke: (t) => (t.vars ?? t).palette[tone].main,
            transition: 'stroke-dasharray 400ms',
          }}
        />
      </Box>
      <Stack
        sx={{
          position: 'absolute',
          inset: 0,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {children}
      </Stack>
    </Box>
  );
}

function DateTile({
  icon,
  label,
  value,
  hint,
  strong,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
}) {
  return (
    <Stack
      title={hint}
      direction="row"
      spacing={1.25}
      sx={{
        alignItems: 'center',
        p: 1.5,
        borderRadius: '12px',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ color: strong ? 'primary.main' : 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: strong ? 750 : 650 }} noWrap>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

/** Activation-code entry, formatted as XXXX-XXXX-XXXX-XXXX while typing. */
export function RedeemCard({
  onRedeemed,
  locked,
}: {
  onRedeemed: (r: RedeemResult) => void;
  locked: boolean;
}) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canManage = can('subscription.manage');
  const [code, setCode] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const complete = isCompleteActivationCode(code);

  const redeem = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/subscription/redeem', { params: idempotency(), body: { code } })),
    onSuccess: async (r) => {
      setCode('');
      setProblem(null);
      await qc.invalidateQueries({ queryKey: keys.subscription });
      onRedeemed(r);
    },
    onError: (e) => {
      setProblem(e instanceof ApiError ? redeemErrorText(e.code) : errorMessage(e));
    },
  });

  return (
    <Card
      sx={(t) => ({
        borderColor: locked ? (t.vars ?? t).palette.error.main : undefined,
      })}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
          <IconTile color={locked ? 'error' : 'primary'}>
            <RedeemRounded />
          </IconTile>
          <Box>
            <Typography variant="h5">Redeem activation code</Typography>
            <Typography variant="body2" color="text.secondary">
              Extends from your paid-through date, or from today if it has passed.
            </Typography>
          </Box>
        </Stack>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!complete) {
              setProblem('Enter all 16 characters of the code.');
              return;
            }
            redeem.mutate();
          }}
        >
          <TextField
            label="Activation code"
            placeholder="XXXX-XXXX-XXXX-XXXX"
            value={code}
            disabled={!canManage || redeem.isPending}
            onChange={(e) => {
              setCode(formatActivationCode(e.target.value));
              setProblem(null);
            }}
            error={Boolean(problem)}
            helperText={
              problem ??
              (canManage
                ? 'Letters and numbers only — dashes are added for you.'
                : 'Only the owner (or someone with “Manage subscription”) can redeem codes.')
            }
            slotProps={{
              htmlInput: {
                autoComplete: 'off',
                spellCheck: false,
                'aria-label': 'Activation code',
                style: {
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  letterSpacing: '0.12em',
                  fontSize: 18,
                  fontWeight: 600,
                },
              },
              input: { startAdornment: <KeyRounded sx={{ mr: 1, color: 'text.disabled' }} /> },
            }}
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            loading={redeem.isPending}
            disabled={!canManage || code.length === 0}
            startIcon={<AutorenewRounded />}
            sx={{ mt: 2 }}
          >
            Redeem code
          </Button>
        </form>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Don’t have a code? Contact PackBytes to renew. Codes work once and only for this shop.
        </Typography>
      </CardContent>
    </Card>
  );
}

const EVENT_ICONS: Record<SubscriptionEventKind, ReactNode> = {
  CREATED: <StorefrontRounded />,
  RENEWED: <AutorenewRounded />,
  EXTENDED: <WorkspacePremiumRounded />,
  CODE_REDEEMED: <RedeemRounded />,
  SUSPENDED: <PauseCircleRounded />,
  RESUMED: <PlayCircleRounded />,
  UPDATED: <HistoryRounded />,
  CORRECTED: <EditCalendarRounded />,
  REVERSED: <EventBusyRounded />,
};

function History({ events }: { events: Schemas['SubscriptionEvent'][] }) {
  const { format } = useShop();
  const sorted = [...events].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="h5">History</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Every change to your subscription period.
        </Typography>
        {sorted.length === 0 ? (
          <Typography color="text.secondary">No changes yet.</Typography>
        ) : (
          <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {sorted.map((e, i) => {
              const tone =
                e.kind === 'SUSPENDED' || e.kind === 'REVERSED'
                  ? 'error'
                  : e.kind === 'CREATED'
                    ? 'info'
                    : e.kind === 'CORRECTED'
                      ? 'warning'
                      : 'success';
              return (
                <Stack
                  component="li"
                  key={e.id}
                  direction="row"
                  spacing={2}
                  sx={{ position: 'relative', pb: i === sorted.length - 1 ? 0 : 2.5 }}
                >
                  {i < sorted.length - 1 && (
                    <Box
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        left: 17,
                        top: 40,
                        bottom: 4,
                        width: 2,
                        borderRadius: 1,
                        bgcolor: 'divider',
                      }}
                    />
                  )}
                  <IconTile size={36} color={tone}>
                    {EVENT_ICONS[e.kind]}
                  </IconTile>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      sx={{ justifyContent: 'space-between', gap: 0.5 }}
                    >
                      <Typography sx={{ fontWeight: 650 }}>
                        {SUBSCRIPTION_EVENT_LABELS[e.kind]}
                        {/* A correction's days are the new total, not days added. */}
                        {e.daysAdded && e.kind !== 'CORRECTED' ? (
                          <Box
                            component="span"
                            sx={{ color: 'success.main', fontWeight: 700, ml: 1 }}
                            className="tabular"
                          >
                            +{plural(e.daysAdded, 'day')}
                          </Box>
                        ) : null}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dateTime(e.createdAt, format)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {e.notes ??
                        `Paid through ${businessDate(e.paidThroughAfter.slice(0, 10), format)}`}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function RedeemedDialog({ result, onClose }: { result: RedeemResult; onClose: () => void }) {
  const { format } = useShop();
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <IconTile size={64} color="success">
            <CelebrationRounded />
          </IconTile>
        </Box>
        <Typography variant="h4">{plural(result.daysAdded, 'day')} added</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          You’re now paid through{' '}
          <Box component="strong" sx={{ color: 'text.primary' }}>
            {businessDate(
              result.subscription?.paidThroughDate ?? result.paidThroughAfter.slice(0, 10),
              format,
            )}
          </Box>
          . Thank you!
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center' }}>
        <Button variant="contained" onClick={onClose} sx={{ minWidth: 140 }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SubscriptionSkeleton() {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '1.5fr 1fr' },
      }}
    >
      <Card sx={{ p: 3.5 }}>
        <Stack direction="row" spacing={3.5} sx={{ alignItems: 'center' }}>
          <Skeleton variant="circular" width={128} height={128} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={120} height={28} />
            <Skeleton width="60%" height={40} />
            <Skeleton width="80%" />
          </Box>
        </Stack>
        <Skeleton variant="rounded" height={64} sx={{ mt: 3 }} />
      </Card>
      <Card sx={{ p: 3 }}>
        <Skeleton width="60%" height={32} />
        <Skeleton variant="rounded" height={56} sx={{ mt: 2 }} />
        <Skeleton variant="rounded" height={44} sx={{ mt: 2 }} />
      </Card>
    </Box>
  );
}
